import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BioMechanicsDrawer from './BioMechanicsDrawer';
import TrainingStatsDrawer from './TrainingStatsDrawer';
import axios from 'axios';
import apiClient from '../utils/apiClient';
import { getUser, getAuthToken, successMessage, errorMessage } from '../utils/helpers';
import { Folder, FolderOpen, ChevronRight, ChevronDown, Activity, Database, X, Zap, Target, FolderPlus, Loader, Check, AlertCircle, Upload, Trash2 } from 'lucide-react';
import { ConfirmDialog } from './design-system';
import VideoDisplayPanel from './VideoDisplayPanel';
import { fetchVideoAnalysis, getSelectedSport } from '../utils/helpers';
import { deleteVideoAnalytics } from '../utils/videoAnalyticsApi';
import VideoCsv from './VideoCsv';
import { NODE_BACKEND_URL } from '../utils/constant';
import { useSportContext } from '../hooks/useSportContext';

const SystemIntegration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Sport context - reactive to Settings changes
  const {
    sportCode,
    sportName,
    teamName,
    isBaseball,
    isBasketball,
  } = useSportContext();

  const [expandedFolders, setExpandedFolders] = useState(new Set(['root']));
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderError, setFolderError] = useState('');
  const [folderSuccess, setFolderSuccess] = useState('');

  // Video display states
  const [videoData, setVideoData] = useState([]);
  const [totalVideos, setTotalVideos] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [showCsvViewer, setShowCsvViewer] = useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = useState(null);
  const [csvViewerData, setCsvViewerData] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [trainingStatsDrawerOpen, setTrainingStatsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [dataCategories, setDataCategories] = useState({
    'Training Statistics': {
      id: '9f8d6c1e-12a3-4c0a-bf62-3c5f5cba92e7',
      icon: <Target className="w-4 h-4" />,
      color: 'text-blue-500',
      source_type: 'training_statistics',
      folders: {}
    },
    'Video Analytics': {
      id: '4d72b5e0-88c4-46a1-9a43-5a3e9d8c7129',
      icon: <Activity className="w-4 h-4" />,
      color: 'text-green-500',
      source_type: 'video_analytics',
      folders: {}
    },
    'Physical Assessment & Strength/Power': {
      id: 'c31a7d5f-2f64-42a0-a6e1-1c44d8b6f38a',
      icon: <Zap className="w-4 h-4" />,
      color: 'text-orange-500',
      folders: {},
      source_type: 'physical_assessment',
    }
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("is_video_analytics") == "true") {
      toggleFolder("Video Analytics");
    }
    else if(params.get("is_stats") == "true"){
      toggleFolder("Training Statistics");
    }
  }, [location.search]);

  // Re-fetch data when sport changes from Settings
  useEffect(() => {
    if (sportCode) {
      console.log('[SystemIntegrations] Sport changed to:', sportCode, teamName);
      fetchFolders();
    }
  }, [sportCode]);

  // Fetch folders from API
  const fetchFolders = async () => {
    setIsLoading(true);
    setFetchError(null);

    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.get(`${NODE_BACKEND_URL}/api/get-folders?user_id=${getUser()?.id}`, {
        headers: { Authorization: getAuthToken() }
      });

      if (response?.data?.data) {
        processFolders(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setFetchError('Failed to load folders. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Process and organize folders by parent_id
  const processFolders = (folders) => {
    if (!Array.isArray(folders) || folders.length === 0) {
      return;
    }

    const updatedCategories = { ...dataCategories };
    const folderMap = {};

    // Process each folder
    folders.forEach(folder => {
      const { name, id, parent_id, status = 'Active', created_at } = folder;

      // Add to folder map for global access
      folderMap[name] = {
        id,
        status,
        created_at
      };

      // Find the category that matches the parent_id
      Object.entries(updatedCategories).forEach(([categoryName, categoryData]) => {
        if (categoryData.id === parent_id) {
          // Add the folder to this category
          updatedCategories[categoryName].folders[name] = {
            id,
            status,
            lastRun: created_at || new Date().toLocaleString(),
            icon: <Folder className="w-4 h-4" />
          };
        }
      });
    });

    // Make folders accessible globally for VideoDisplayPanel to use
    window.folderData = folderMap;

    // Update state with processed folders
    setDataCategories(updatedCategories);
  };

  // Check URL params on component mount and fetch data
  useEffect(() => {
    fetchFolders();

    // Check if folder_id is in URL
    const folderIdFromUrl = searchParams.get('folder_id');
    const sourceTypeFromUrl = searchParams.get('source_type');

    if (folderIdFromUrl) {
      setCurrentFolderId(folderIdFromUrl);

      // Find the folder name that matches this ID
      setTimeout(() => {
        Object.entries(dataCategories).forEach(([categoryName, categoryData]) => {
          Object.entries(categoryData.folders).forEach(([folderName, folderData]) => {
            if (folderData.id === folderIdFromUrl) {
              setCurrentFolder(folderName);
            }
          });
        });

        // Fetch videos for this folder directly on page load
        fetchVideosForFolder(folderIdFromUrl, sourceTypeFromUrl);
      }, 500); // Small delay to ensure dataCategories has loaded
    }
  }, []);

  // Update URL when folder changes
  useEffect(() => {
    if (currentFolderId) {
      // Find the source_type for this folder
      let sourceType = '';
      Object.entries(dataCategories).forEach(([categoryName, categoryData]) => {
        Object.entries(categoryData.folders).forEach(([folderName, folderData]) => {
          if (folderData.id === currentFolderId) {
            sourceType = categoryData.source_type || categoryName.toLowerCase().replace(/\s+/g, '_');
          }
        });
      });

      // Update URL without reloading the page, include source_type if available
      const url = sourceType
        ? `/integrations?folder_id=${currentFolderId}&source_type=${sourceType}`
        : `/integrations?folder_id=${currentFolderId}`;

      navigate(url, { replace: true });

      // Fetch videos for this folder
      fetchVideosForFolder(currentFolderId, sourceType);
    }
  }, [currentFolderId, currentPage, pageSize, navigate, dataCategories]);

  // Fetch videos for a folder
  const fetchVideosForFolder = async (folderId, sourceType = null) => {
    if (!folderId) return;

    setIsLoadingVideos(true);
    try {
      const videos = await fetchVideoAnalysis(null, currentPage, pageSize, folderId, sourceType);
      setVideoData(videos?.videos || []);
      setTotalVideos(videos?.total || 0);
    } catch (error) {
      console.error('Error fetching videos for folder:', error);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  // Handle page change for video pagination
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  // Handle folder deletion
  const handleDeleteFolder = async (folderId, folderName) => {
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.post(`${NODE_BACKEND_URL}/api/delete-folder`, {
        id: folderId,
        user_id: getUser()?.id
      }, {
        headers: { Authorization: getAuthToken() }
      });

      if (response.data?.success) {
        successMessage(`Folder "${folderName}" deleted successfully`);

        // Update dataCategories by filtering out the deleted folder locally
        const updatedCategories = { ...dataCategories };
        
        // Find which category contains this folder and remove it
        Object.entries(updatedCategories).forEach(([categoryName, categoryData]) => {
          if (Object.entries(categoryData.folders).some(([name, data]) => data.id === folderId)) {
            // Create a new folders object without the deleted folder
            const updatedFolders = {};
            Object.entries(categoryData.folders).forEach(([name, data]) => {
              if (data.id !== folderId) {
                updatedFolders[name] = data;
              }
            });
            
            // Update the category with the filtered folders
            updatedCategories[categoryName] = {
              ...categoryData,
              folders: updatedFolders
            };
          }
        });
        
        // Update state with the filtered categories
        setDataCategories(updatedCategories);

        // If the current folder is deleted, reset selection
        if (currentFolderId === folderId) {
          setCurrentFolder(null);
          setCurrentFolderId(null);
          navigate('/integrations', { replace: true });
        }
      } else {
        errorMessage(response.data?.message || 'Failed to delete folder');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      errorMessage('Error deleting folder. Please try again.');
    }
  };

  // Handle video deletion
  const handleDeleteVideo = async (videoId) => {
    try {
      await axios.post(`${NODE_BACKEND_URL}/api/delete-video-analysis`, {
        id: videoId,
      }, {
        headers: { Authorization: getAuthToken() }
      });      // Remove the deleted video from the list
      setVideoData(prevVideos => prevVideos.filter(v => v.id !== videoId));
      // Update total count
      setTotalVideos(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  };

  // Handle CSV view
  const handleViewCsv = async (video) => {
    if (!video?.csv_id) return;

    setSelectedCsvFile(video);
    try {
      const url = `https://slam-all-python-359065791766.us-central1.run.app/${getSelectedSport()}/video-analysis/plays/${video.csv_id}`;
      const res = await apiClient.get(url);
      const data = res?.data;

      if (data?.success) {
        setCsvViewerData(data?.data);
        setShowCsvViewer(true);
      } else {
        console.warn('CSV API response error:', data?.message);
      }
    } catch (error) {
      console.error('Error loading CSV data:', error);
    }
  };

  // Handle closing CSV viewer
  const handleCloseCsvViewer = () => {
    setShowCsvViewer(false);
    setCsvViewerData(null);
    setSelectedCsvFile(null);
  };

  const toggleFolder = (folder) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folder)) {
      newExpanded.delete(folder);
    } else {
      newExpanded.add(folder);
    }
    setExpandedFolders(newExpanded);
  };

  const handleCreateFolder = async () => {
    // Validate folder name
    if (!newFolderName.trim()) {
      setFolderError('Please enter a folder name');
      return;
    }

    setIsCreatingFolder(true);
    setFolderError('');
    setFolderSuccess('');

    try {
      // Get the category ID for the current category
      const categoryId = dataCategories[currentCategory]?.id;
      const sourceType = dataCategories[currentCategory]?.source_type || currentCategory.toLowerCase().replace(/\s+/g, '_');

      if (!categoryId) {
        setFolderError('Invalid category selection');
        return;
      }

      // Make API call to create the folder - using same API as VideoAnalytics.js
      const response = await axios.post(`${NODE_BACKEND_URL}/api/create-folder`, {
        name: newFolderName.trim(),
        parent_id: categoryId,  // Use the category ID as the parent_id
        user_id: getUser()?.id,
      }, {
        headers: { Authorization: getAuthToken() }
      });

      if (response?.data?.success) {
        // Show success message
        setFolderSuccess(`Folder "${newFolderName}" created successfully!`);
        setNewFolderName('');

        setShowFolderModal(false);
        await fetchFolders();
      } else {
        setFolderError(response?.data?.message || 'Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      setFolderError(error?.response?.data?.message || 'An error occurred while creating the folder');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // If showing CSV viewer, render the VideoCsv component
  if (showCsvViewer && selectedCsvFile) {
    return (
      <VideoCsv
        playsData={csvViewerData}
        videoUrl={selectedCsvFile?.secure_url}
        onBack={handleCloseCsvViewer}
      />
    );
  }

  return (
    <>
      {/* Overlay when drawer is open - clicking it closes the drawer */}
      {(drawerOpen || trainingStatsDrawerOpen) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40" 
          onClick={() => {
            if (drawerOpen) setDrawerOpen(false);
            if (trainingStatsDrawerOpen) setTrainingStatsDrawerOpen(false);
          }}
        />
      )}

      <div className="flex bg-gray-50 relative page-container">
        {/* Left Panel - File Explorer */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Data Sources</h2>
          <p className="text-sm text-gray-500 mt-1">Upload and manage analytics data</p>
        </div>

        <div className="overflow-y-auto flex-1-auto p-4">
          {/* Training Statistics */}
          <div className="mb-4">
            <div
              className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
              onClick={() => toggleFolder('Training Statistics')}
            >
              {expandedFolders.has('Training Statistics') ?
                <ChevronDown className="w-4 h-4 text-gray-400" /> :
                <ChevronRight className="w-4 h-4 text-gray-400" />
              }
              <span className="text-blue-500"><Target className="w-4 h-4" /></span>
              <span className="font-medium text-sm text-gray-700">Training Statistics</span>
              <button
                className="ml-auto p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentCategory('Training Statistics');
                  setShowFolderModal(true);
                  setNewFolderName('');
                  setFolderError('');
                  setFolderSuccess('')
                }}
                title="Create new folder"
              >
                <FolderPlus size={16} className="text-gray-600" />
              </button>
            </div>

            {expandedFolders.has('Training Statistics') && (
              <div className="ml-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader size={16} className="text-blue-500 animate-spin mr-2" />
                    <span className="text-sm text-gray-500">Loading folders...</span>
                  </div>
                ) : fetchError ? (
                  <div className="text-sm text-red-500 py-2 px-1">
                    <AlertCircle size={14} className="inline mr-1" />
                    {fetchError}
                  </div>
                ) : Object.entries(dataCategories['Training Statistics'].folders).length === 0 ? (
                  <div className="text-sm text-gray-500 py-2 px-1">
                    No folders found. Create a new folder to get started.
                  </div>
                ) : Object.entries(dataCategories['Training Statistics'].folders).map(([folderName, folderData]) => (
                  <div key={folderName} className="mb-2">
                    <div
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      onClick={() => {
                        setCurrentFolder(folderName);
                        setCurrentFolderId(folderData.id);
                        setTrainingStatsDrawerOpen(false);
                        setDrawerOpen(false);
                      }}
                    >
                      <Folder className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-700 flex-1">{folderName}</span>
                      <div className="flex items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentFolder(folderName);
                            setCurrentFolderId(folderData.id);
                            setTrainingStatsDrawerOpen(true);
                            setDrawerOpen(false);
                          }}
                          className="p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors mr-1"
                          title="Upload to this folder"
                        >
                          <Upload size={16} className="text-blue-600" />
                        </button>
                        <ConfirmDialog
                          title="Delete Folder"
                          description="Are you sure you want to delete this folder and all its contents?"
                          onConfirm={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folderData.id, folderName);
                          }}
                          okText="Yes"
                          cancelText="No"
                          onCancel={(e) => e.stopPropagation()}
                          destructive
                        >
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors"
                            title="Delete folder"
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </ConfirmDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Video Analytics */}
          <div className="mb-4">
            <div
              className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
              onClick={() => toggleFolder('Video Analytics')}
            >
              {expandedFolders.has('Video Analytics') ?
                <ChevronDown className="w-4 h-4 text-gray-400" /> :
                <ChevronRight className="w-4 h-4 text-gray-400" />
              }
              <span className="text-green-500"><Activity className="w-4 h-4" /></span>
              <span className="font-medium text-sm text-gray-700">Video Analytics</span>
              <button
                className="ml-auto p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentCategory('Video Analytics');
                  setShowFolderModal(true);
                  setNewFolderName('');
                  setFolderError('');
                  setFolderSuccess('')
                }}
                title="Create new folder"
              >
                <FolderPlus size={16} className="text-gray-600" />
              </button>
            </div>

            {expandedFolders.has('Video Analytics') && (
              <div className="ml-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader size={16} className="text-green-500 animate-spin mr-2" />
                    <span className="text-sm text-gray-500">Loading folders...</span>
                  </div>
                ) : fetchError ? (
                  <div className="text-sm text-red-500 py-2 px-1">
                    <AlertCircle size={14} className="inline mr-1" />
                    {fetchError}
                  </div>
                ) : Object.entries(dataCategories['Video Analytics'].folders).length === 0 ? (
                  <div className="text-sm text-gray-500 py-2 px-1">
                    No folders found. Create a new folder to get started.
                  </div>
                ) : Object.entries(dataCategories['Video Analytics'].folders).map(([folderName, folderData]) => (
                  <div key={folderName} className="mb-2">
                    <div
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      onClick={() => {
                        setCurrentFolder(folderName);
                        setCurrentFolderId(folderData.id);
                        setTrainingStatsDrawerOpen(false);
                        setDrawerOpen(false);
                      }}
                    >
                      <Folder className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-700 flex-1">{folderName}</span>
                      <div className="flex items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentFolder(folderName);
                            setCurrentFolderId(folderData.id);
                            setTrainingStatsDrawerOpen(false);
                            setDrawerOpen(true);
                          }}
                          className="p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors mr-1"
                          title="Upload to this folder"
                        >
                          <Upload size={16} className="text-blue-600" />
                        </button>
                        <ConfirmDialog
                          title="Delete Folder"
                          description="Are you sure you want to delete this folder and all its contents?"
                          onConfirm={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folderData.id, folderName);
                          }}
                          okText="Yes"
                          cancelText="No"
                          onCancel={(e) => e.stopPropagation()}
                          destructive
                        >
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors"
                            title="Delete folder"
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </ConfirmDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Physical Assessment & Strength/Power */}
          <div className="mb-4">
            <div
              className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
              onClick={() => toggleFolder('Physical Assessment & Strength/Power')}
            >
              {expandedFolders.has('Physical Assessment & Strength/Power') ?
                <ChevronDown className="w-4 h-4 text-gray-400" /> :
                <ChevronRight className="w-4 h-4 text-gray-400" />
              }
              <span className="text-orange-500"><Zap className="w-4 h-4" /></span>
              <span className="font-medium text-sm text-gray-700">Physical Assessment & Strength/Power</span>
              <button
                className="ml-auto p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentCategory('Physical Assessment & Strength/Power');
                  setShowFolderModal(true);
                  setNewFolderName('');
                  setFolderError('');
                  setFolderSuccess('')
                }}
                title="Create new folder"
              >
                <FolderPlus size={16} className="text-gray-600" />
              </button>
            </div>

            {expandedFolders.has('Physical Assessment & Strength/Power') && (
              <div className="ml-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader size={16} className="text-orange-500 animate-spin mr-2" />
                    <span className="text-sm text-gray-500">Loading folders...</span>
                  </div>
                ) : fetchError ? (
                  <div className="text-sm text-red-500 py-2 px-1">
                    <AlertCircle size={14} className="inline mr-1" />
                    {fetchError}
                  </div>
                ) : Object.entries(dataCategories['Physical Assessment & Strength/Power'].folders).length === 0 ? (
                  <div className="text-sm text-gray-500 py-2 px-1">
                    No folders found. Create a new folder to get started.
                  </div>
                ) : Object.entries(dataCategories['Physical Assessment & Strength/Power'].folders).map(([folderName, folderData]) => (
                  <div key={folderName} className="mb-2">
                    <div
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      onClick={() => {
                        setCurrentFolder(folderName);
                        setCurrentFolderId(folderData.id);
                        setTrainingStatsDrawerOpen(false);
                        setDrawerOpen(false);
                      }}
                    >
                      <Folder className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-700 flex-1">{folderName}</span>
                      <div className="flex items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentFolder(folderName);
                            setCurrentFolderId(folderData.id);
                            if (currentCategory === 'Training Statistics') {
                              setTrainingStatsDrawerOpen(true);
                              setDrawerOpen(false);
                            } else {
                              setDrawerOpen(false);
                            }
                          }}
                          className="p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors mr-1"
                          title="Upload to this folder"
                        >
                          <Upload size={16} className="text-blue-600" />
                        </button>
                        <ConfirmDialog
                          title="Delete Folder"
                          description="Are you sure you want to delete this folder and all its contents?"
                          onConfirm={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folderData.id, folderName);
                          }}
                          okText="Yes"
                          cancelText="No"
                          onCancel={(e) => e.stopPropagation()}
                          destructive
                        >
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 rounded-full hover:bg-gray-200 focus:outline-none transition-colors"
                            title="Delete folder"
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </ConfirmDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - Video Display */}
      <div className="flex-1 bg-white relative z-20">
        {(currentFolder || currentFolderId) ? (
          <VideoDisplayPanel
            videos={videoData}
            totalCount={totalVideos}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onRefresh={() => fetchVideosForFolder(currentFolderId)}
            isLoading={isLoadingVideos}
            onDeleteVideo={handleDeleteVideo}
            onViewCsv={handleViewCsv}
            folderId={currentFolderId}
            folderName={currentFolder}
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No folder selected</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Select a folder from the explorer to view its videos
              </p>
            </div>
          </div>
        )}
      </div>
      {/* BioMechanics Drawer */}
      <BioMechanicsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentFolder={currentFolder}
        currentFolderId={currentFolderId}
        onUploadComplete={() => {
          setDrawerOpen(false)
          fetchVideosForFolder(currentFolderId)
        }}
      />

      {/* Training Statistics Drawer */}
      <TrainingStatsDrawer
        open={trainingStatsDrawerOpen}
        onClose={() => setTrainingStatsDrawerOpen(false)}
        currentFolder={currentFolder}
        currentFolderId={currentFolderId}
        onUploadComplete={() => {
          setTrainingStatsDrawerOpen(false);
          fetchVideosForFolder(currentFolderId);
        }}
      />

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Folder</h3>
              <button
                onClick={() => setShowFolderModal(false)}
                className="p-1 rounded-full hover:bg-gray-200 focus:outline-none"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {folderError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md flex items-start">
                <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{folderError}</p>
              </div>
            )}

            {folderSuccess && (
              <div className="mb-4 p-3 bg-green-100 border border-green-200 text-green-700 rounded-md flex items-start">
                <Check size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{folderSuccess}</p>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                {currentCategory}
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 mb-1">
                Folder Name
              </label>
              <input
                type="text"
                id="folderName"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter folder name"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowFolderModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={isCreatingFolder || !newFolderName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isCreatingFolder ? (
                  <>
                    <Loader size={16} className="mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Folder'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default SystemIntegration;